import * as THREE from 'three';
import { computeGeometricAdjacency, edgeId } from './mesh-topology.js';

const DEFAULT_COLOR = new THREE.Color(0x4f9de0);
const SELECTED_COLOR = new THREE.Color(0xffb020);
// Scratch objects for the edge-picking hot path — avoids allocating a new
// THREE.Line3/Vector3 per candidate edge per raycast.
const pickLine = new THREE.Line3();
const pickClosestPoint = new THREE.Vector3();

/**
 * Displays and picks vertices/edges/faces for the mesh object currently in
 * `meshSelection`, and keeps that selection's click-to-select in sync.
 *
 * Deliberately reads topology straight off `object.userData.anvilParams.topology`
 * rather than going through a command: this is a same-process, same-browser
 * read of state the browser already owns, exactly how `selection.js` reads
 * `registry.list()` directly rather than issuing `listObjects`. Only the
 * eventual *mutation* (in mesh-edit-tools.js) goes through the bridge.
 *
 * Helper geometry lives in `viewport.helpers` (never exported, see scene.js)
 * and is rebuilt wholesale on `refresh()` rather than kept in sync
 * incrementally — the same choice `selection.js`'s BoxHelper approach makes.
 * The active object's transform is baked into each helper's matrix once per
 * rebuild; that's safe because the object's transform cannot change while
 * mesh Edit Mode is active (object-level transform tools are suspended for
 * the duration — see mesh-edit-tools.js / main.js).
 */
export function createMeshEditView({ viewport, registry, meshSelection }) {
  const helpers = new THREE.Group();
  helpers.name = 'Mesh edit helpers';
  viewport.helpers.add(helpers);

  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  let pointerStart = null;
  let ignoreNextClick = false;
  let destroyed = false;
  let geometricAdjacency = null;
  let geometricEdgeIdByTopologyEdgeId = new Map();

  function activeObject() {
    const id = meshSelection.meshId;
    return id ? registry.get(id) : null;
  }

  function activeTopology() {
    return activeObject()?.userData?.anvilParams?.topology ?? null;
  }

  function disposeChildren() {
    for (const child of [...helpers.children]) {
      helpers.remove(child);
      child.geometry?.dispose();
      child.material?.dispose();
    }
  }

  function bakeWorldMatrix(object3d, worldMatrix) {
    object3d.matrixAutoUpdate = false;
    object3d.matrix.copy(worldMatrix);
    object3d.matrixWorldNeedsUpdate = true;
  }

  function buildVertexHelper(object, topology) {
    const positions = new Float32Array(topology.vertices.length * 3);
    const colors = new Float32Array(topology.vertices.length * 3);
    topology.vertices.forEach((vertex, index) => {
      positions.set(vertex.position, index * 3);
      const color = meshSelection.has(vertex.id) ? SELECTED_COLOR : DEFAULT_COLOR;
      colors.set([color.r, color.g, color.b], index * 3);
    });
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const points = new THREE.Points(geometry, new THREE.PointsMaterial({
      size: 8, sizeAttenuation: false, vertexColors: true, depthTest: false,
    }));
    points.name = 'Mesh vertex markers';
    points.renderOrder = 999;
    bakeWorldMatrix(points, object.matrixWorld);
    helpers.add(points);
  }

  function buildEdgeHelper(object, adjacency) {
    const edges = adjacency.edges;
    const byId = new Map(adjacency.corners.map((corner) => [corner.id, corner]));
    const positions = new Float32Array(edges.length * 6);
    const colors = new Float32Array(edges.length * 6);
    edges.forEach((edge, index) => {
      const a = byId.get(edge.cornerIds[0]).position;
      const b = byId.get(edge.cornerIds[1]).position;
      positions.set(a, index * 6);
      positions.set(b, index * 6 + 3);
      const color = meshSelection.has(edge.id) ? SELECTED_COLOR : DEFAULT_COLOR;
      colors.set([color.r, color.g, color.b, color.r, color.g, color.b], index * 6);
    });
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const lines = new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({
      vertexColors: true, depthTest: false, linewidth: 2,
    }));
    lines.name = 'Mesh edge markers';
    lines.renderOrder = 998;
    bakeWorldMatrix(lines, object.matrixWorld);
    helpers.add(lines);
  }

  function buildFaceHighlight(object, topology) {
    const selectedFaces = topology.faces.filter((face) => meshSelection.has(face.id));
    if (selectedFaces.length === 0) return;
    const byId = new Map(topology.vertices.map((vertex) => [vertex.id, vertex]));
    const positions = new Float32Array(selectedFaces.length * 9);
    selectedFaces.forEach((face, faceIndex) => {
      face.vertexIds.forEach((vertexId, corner) => {
        positions.set(byId.get(vertexId).position, faceIndex * 9 + corner * 3);
      });
    });
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const highlight = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({
      color: SELECTED_COLOR, transparent: true, opacity: 0.45, depthTest: false, side: THREE.DoubleSide,
    }));
    highlight.name = 'Mesh face highlight';
    highlight.renderOrder = 997;
    bakeWorldMatrix(highlight, object.matrixWorld);
    helpers.add(highlight);
  }

  /** Rebuild every helper from the current topology and selection. Safe to call whether or not edit mode is active. */
  function refresh() {
    if (destroyed) return;
    disposeChildren();
    const object = activeObject();
    const topology = activeTopology();
    geometricAdjacency = null;
    geometricEdgeIdByTopologyEdgeId = new Map();
    if (!object || !topology) return;
    object.updateWorldMatrix(true, false);
    geometricAdjacency = computeGeometricAdjacency(topology);
    for (const edge of geometricAdjacency.edges) {
      for (const topologyEdgeId of edge.topologyEdgeIds) {
        geometricEdgeIdByTopologyEdgeId.set(topologyEdgeId, edge.id);
      }
    }

    // Vertices always render (they're the pick targets for edge/face corners
    // too, and the cheapest way to see "this is an editable mesh"); the
    // element-mode-specific layer renders alongside them.
    buildVertexHelper(object, topology);
    if (meshSelection.elementType === 'edge') buildEdgeHelper(object, geometricAdjacency);
    else if (meshSelection.elementType === 'face') buildFaceHighlight(object, topology);
  }

  /**
   * Raycast against the active mesh's real geometry (not the decorative
   * helpers) and resolve the hit down to the nearest element of the current
   * mode. `intersection.faceIndex` maps 1:1 to `topology.faces[faceIndex]`
   * because every face was built as exactly one triangle (mesh-topology.js).
   */
  function pickAt(event) {
    const object = activeObject();
    const topology = activeTopology();
    if (!object || !topology) return null;

    const bounds = viewport.renderer.domElement.getBoundingClientRect();
    pointer.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    pointer.y = -((event.clientY - bounds.top) / bounds.height) * 2 + 1;
    raycaster.setFromCamera(pointer, viewport.camera);
    const hit = raycaster.intersectObject(object, false)[0];
    if (!hit || hit.faceIndex == null) return null;
    const face = topology.faces[hit.faceIndex];
    if (!face) return null;
    if (meshSelection.elementType === 'face') return face.id;

    const byId = new Map(topology.vertices.map((vertex) => [vertex.id, vertex]));
    object.updateWorldMatrix(true, false);
    const worldPositionOf = (vertexId) => new THREE.Vector3(...byId.get(vertexId).position).applyMatrix4(object.matrixWorld);

    if (meshSelection.elementType === 'vertex') {
      let nearestId = null;
      let nearestDistance = Infinity;
      for (const vertexId of face.vertexIds) {
        const distance = worldPositionOf(vertexId).distanceTo(hit.point);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestId = vertexId;
        }
      }
      return nearestId;
    }

    // edge mode: nearest of the triangle's 3 edges to the hit point.
    const [a, b, c] = face.vertexIds;
    let nearestTopologyEdgeId = null;
    let nearestDistance = Infinity;
    for (const [fromId, toId] of [[a, b], [b, c], [c, a]]) {
      pickLine.set(worldPositionOf(fromId), worldPositionOf(toId));
      pickLine.closestPointToPoint(hit.point, true, pickClosestPoint);
      const distance = pickClosestPoint.distanceTo(hit.point);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestTopologyEdgeId = edgeId(fromId, toId);
      }
    }
    return mapTopologyEdgeIdToGeometricId(nearestTopologyEdgeId);
  }

  function mapTopologyEdgeIdToGeometricId(topologyEdgeId) {
    return topologyEdgeId ? geometricEdgeIdByTopologyEdgeId.get(topologyEdgeId) ?? null : null;
  }

  function currentElementIds() {
    const topology = activeTopology();
    if (!topology) return [];
    if (meshSelection.elementType === 'vertex') return topology.vertices.map((vertex) => vertex.id);
    if (meshSelection.elementType === 'face') return topology.faces.map((face) => face.id);
    if (meshSelection.elementType === 'edge') return geometricAdjacency?.edges.map((edge) => edge.id) ?? [];
    return [];
  }

  // Click-vs-drag disambiguation mirrors selection.js's own pointer tracking
  // — orbiting the camera is a down/drag/up on this same canvas and must not
  // register as a pick. Kept separate from selection.js's copy because it
  // targets mesh elements, not registry ids, and only runs while edit mode
  // is active.
  function startPointer(event) {
    if (event.button !== 0) return;
    ignoreNextClick = false;
    pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
  }

  function trackPointer(event) {
    if (!pointerStart || event.pointerId !== pointerStart.id) return;
    pointerStart.moved ||= Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y) > 4;
  }

  function finishPointer(event) {
    if (!pointerStart || event.pointerId !== pointerStart.id) return;
    ignoreNextClick = pointerStart.moved;
    pointerStart = null;
  }

  function clickAt(event) {
    if (!meshSelection.active) return;
    if (event.button != null && event.button !== 0) return;
    if (ignoreNextClick) {
      ignoreNextClick = false;
      return;
    }
    const id = pickAt(event);
    if (id) meshSelection.select(id, { toggle: event.shiftKey });
    else if (!event.shiftKey) meshSelection.clear();
  }

  const canvas = viewport.renderer.domElement;
  canvas.addEventListener('pointerdown', startPointer, true);
  canvas.addEventListener('pointermove', trackPointer, true);
  canvas.addEventListener('pointerup', finishPointer, true);
  canvas.addEventListener('pointercancel', finishPointer, true);
  canvas.addEventListener('click', clickAt);

  return {
    refresh,
    pickAt,
    mapTopologyEdgeIdToGeometricId,
    currentElementIds,
    getGeometricAdjacency() { return geometricAdjacency; },
    destroy() {
      destroyed = true;
      canvas.removeEventListener('pointerdown', startPointer, true);
      canvas.removeEventListener('pointermove', trackPointer, true);
      canvas.removeEventListener('pointerup', finishPointer, true);
      canvas.removeEventListener('pointercancel', finishPointer, true);
      canvas.removeEventListener('click', clickAt);
      disposeChildren();
      helpers.removeFromParent();
    },
  };
}
